import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Building2, Users, BarChart3, Sparkles, Plus, Loader2, ChevronRight,
  Shield, Database, FileText, Layers, Zap, ArrowLeft, Settings, TrendingUp,
  KeyRound, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { PromptDialog } from '@/components/PromptDialog';
import { useConfirmDialog } from '@/components/ConfirmDialog';

export default function TenantAdminPage() {
  const { user, setUserData } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiCredits, setAiCredits] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [creditsPromptOpen, setCreditsPromptOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', domain: '', plan: 'standard', max_users: 10 });
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'viewer' });
  const [tenantUsers, setTenantUsers] = useState([]);
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();

  const isSuperAdmin = user?.role === 'super_admin';

  const handleSendPasswordReset = async (u) => {
    const ok = await confirmFn({
      title: 'Send password reset email?',
      message: `A password-reset link will be emailed to ${u.email}. The link expires in 24 hours.`,
      confirmLabel: 'Send Reset Email',
    });
    if (!ok) return;
    try {
      const res = await api.adminSendPasswordReset(u.user_id);
      if (res.email_sent) {
        toast.success(`Reset email sent to ${u.email}`);
      } else {
        toast.warning(`Reset link generated but SendGrid is not configured — no email was actually sent. Configure SENDGRID_API_KEY to deliver emails.`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email');
    }
  };

  const handleImpersonate = async (u) => {
    const ok = await confirmFn({
      title: 'Impersonate this user?',
      message: `You will be signed in AS ${u.name || u.email} (${u.role}, tenant ${u.tenant_id}). All actions are audited. Use the red banner at the top to exit.`,
      confirmLabel: 'Start Impersonation',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await api.impersonateUser(u.user_id);
      // Persist a marker so the Layout banner's "Exit" button knows to call end-impersonation
      localStorage.setItem('impersonating', JSON.stringify({ admin_user: user, started_at: new Date().toISOString(), return_to: window.location.pathname + window.location.search }));
      // Swap the live user in AuthContext so the Layout banner appears immediately
      setUserData(res.user);
      toast.success(`Impersonating ${u.name || u.email}`);
      // Hard navigate so all in-memory state (TenantContext, ReportTabs, etc.) is rebuilt for the target user
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.message || 'Failed to impersonate user');
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      // Regular admin: show own tenant only
      api.getTenant(user?.tenant_id).then(t => { setTenants([t]); setSelectedTenant(t.tenant_id); loadTenantDetail(t.tenant_id); }).catch(() => {});
      setLoading(false);
      return;
    }
    api.listTenants().then(t => setTenants(t || [])).catch(() => {}).finally(() => setLoading(false));
    // loadTenantDetail is intentionally not a dep — including it would recreate the effect on every render since it's redefined each time.
     
  }, [isSuperAdmin, user?.tenant_id]);

  const loadTenantDetail = async (tenantId) => {
    setSelectedTenant(tenantId);
    try {
      const [detail, usage, credits] = await Promise.all([
        api.getTenant(tenantId).catch(() => null),
        api.getTenantAiUsage(tenantId).catch(() => null),
        api.getTenantAiCredits(tenantId).catch(() => null),
      ]);
      setTenantDetail(detail);
      setAiUsage(usage);
      setAiCredits(credits);
      // Load users
      api.listUsers().then(u => setTenantUsers((u || []).filter(usr => usr.tenant_id === tenantId))).catch(e => console.warn('Failed to load users:', e.message));
    } catch (e) { console.error('Tenant load failed:', e.message); }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.name.trim()) return;
    try {
      const t = await api.createTenant(newTenant);
      setTenants(prev => [...prev, t]);
      setCreateOpen(false);
      setNewTenant({ name: '', domain: '', plan: 'standard', max_users: 10 });
      toast.success(`Tenant "${t.name}" created`);
    } catch (err) { toast.error(err.message); }
  };

  const handleAddUser = async () => {
    if (!newUser.email.trim() || !selectedTenant) return;
    try {
      await api.addUserToTenant(selectedTenant, newUser);
      setAddUserOpen(false);
      setNewUser({ email: '', name: '', password: '', role: 'viewer' });
      loadTenantDetail(selectedTenant);
      toast.success('User added');
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  const planColors = { enterprise: 'bg-indigo-500/15 text-indigo-400', standard: 'bg-emerald-500/15 text-emerald-400', starter: 'bg-amber-500/15 text-amber-400' };

  // Detail view
  if (selectedTenant && tenantDetail) {
    const stats = tenantDetail.stats || {};
    const maxCredits = tenantDetail.ai_credits?.monthly_limit || 0;
    const usedCredits = aiCredits?.used || 0;
    const creditPct = maxCredits > 0 ? Math.min(100, (usedCredits / maxCredits) * 100) : 0;

    return (
      <div className="p-6 max-w-6xl mx-auto" data-testid="tenant-detail">
        <div className="flex items-center gap-3 mb-6">
          {isSuperAdmin && <Button variant="ghost" size="sm" onClick={() => { setSelectedTenant(null); setTenantDetail(null); }} className="gap-1.5"><ArrowLeft className="w-4 h-4" /> All Tenants</Button>}
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold">{tenantDetail.name}</h1>
            <p className="text-sm text-muted-foreground">{tenantDetail.domain || 'No domain set'}</p>
          </div>
          <Badge className={planColors[tenantDetail.plan] || planColors.standard}>{tenantDetail.plan || 'standard'}</Badge>
          <Badge variant="outline" className={tenantDetail.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}>{tenantDetail.status || 'active'}</Badge>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Users className="w-4 h-4" /><span className="text-xs">Users</span></div>
            <p className="text-2xl font-bold">{stats.users || 0}<span className="text-sm font-normal text-muted-foreground ml-1">/ {tenantDetail.max_users || '∞'}</span></p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><FileText className="w-4 h-4" /><span className="text-xs">Reports</span></div>
            <p className="text-2xl font-bold">{stats.reports || 0}</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Database className="w-4 h-4" /><span className="text-xs">Models</span></div>
            <p className="text-2xl font-bold">{stats.models || 0}</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Sparkles className="w-4 h-4" /><span className="text-xs">AI Credits</span></div>
            <p className="text-2xl font-bold">{maxCredits > 0 ? `${usedCredits}` : '∞'}<span className="text-sm font-normal text-muted-foreground ml-1">{maxCredits > 0 ? `/ ${maxCredits}` : 'Unlimited'}</span></p>
          </div>
        </div>

        {/* AI Credit Usage Bar */}
        {maxCredits > 0 && (
          <div className="glass-card rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> AI Credit Usage This Month</h3>
              <span className="text-xs text-muted-foreground">{creditPct.toFixed(0)}% used</span>
            </div>
            <div className="w-full h-3 bg-muted/30 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${creditPct >= 90 ? 'bg-red-500' : creditPct >= 70 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${creditPct}%` }} />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-muted-foreground">{usedCredits} calls used</span>
              <span className="text-[9px] text-muted-foreground">{maxCredits - usedCredits} remaining</span>
            </div>
          </div>
        )}

        {/* AI Usage by Endpoint */}
        {aiUsage?.by_endpoint && Object.keys(aiUsage.by_endpoint).length > 0 && (
          <div className="glass-card rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> AI Usage by Feature</h3>
            <div className="space-y-2">
              {Object.entries(aiUsage.by_endpoint).sort((a, b) => b[1].calls - a[1].calls).map(([ep, data]) => (
                <div key={ep} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-40 truncate">{ep.replace(/_/g, ' ')}</span>
                  <div className="flex-1 h-2 bg-muted/20 rounded-full overflow-hidden">
                    <div className="h-full bg-primary/40 rounded-full" style={{ width: `${Math.min(100, (data.calls / Math.max(...Object.values(aiUsage.by_endpoint).map(d => d.calls))) * 100)}%` }} />
                  </div>
                  <span className="text-xs font-mono text-foreground w-16 text-right">{data.calls} calls</span>
                  <span className="text-[9px] text-muted-foreground w-20 text-right">{data.tokens?.toLocaleString() || 0} tokens</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Monthly Usage */}
        {aiUsage?.monthly?.length > 0 && (
          <div className="glass-card rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold mb-3">Monthly AI Usage</h3>
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border/30">
                  <th className="text-left py-2 text-muted-foreground">Month</th>
                  <th className="text-right py-2 text-muted-foreground">Calls</th>
                  <th className="text-right py-2 text-muted-foreground">Tokens</th>
                  <th className="text-right py-2 text-muted-foreground">Est. Cost</th>
                </tr></thead>
                <tbody>{aiUsage.monthly.map(m => (
                  <tr key={m.month} className="border-b border-border/20">
                    <td className="py-1.5 font-medium">{m.month}</td>
                    <td className="py-1.5 text-right tabular-nums">{m.total_calls?.toLocaleString()}</td>
                    <td className="py-1.5 text-right tabular-nums">{m.total_tokens?.toLocaleString()}</td>
                    <td className="py-1.5 text-right tabular-nums">${m.estimated_cost?.toFixed(4)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={() => setAddUserOpen(true)} variant="outline" className="gap-2"><Plus className="w-4 h-4" /> Add User</Button>
          {isSuperAdmin && <Button onClick={() => setCreditsPromptOpen(true)} variant="outline" className="gap-2" data-testid="set-ai-credits-btn"><Zap className="w-4 h-4" /> Set AI Credits</Button>}
        </div>

        <PromptDialog
          open={creditsPromptOpen} onOpenChange={setCreditsPromptOpen}
          title="Set Monthly AI Credit Limit"
          label={`Monthly limit (current: ${maxCredits || 'unlimited'})`}
          placeholder="e.g. 10000"
          defaultValue={String(maxCredits || 10000)}
          confirmLabel="Update Credits"
          onConfirm={(val) => {
            const n = Number(val);
            if (!n || n < 0) { toast.error('Enter a positive number'); return; }
            api.setTenantAiCredits(selectedTenant, { monthly_limit: n })
              .then(() => { loadTenantDetail(selectedTenant); toast.success('Credits updated'); })
              .catch(e => toast.error(e.message));
          }}
          testId="ai-credits-prompt"
        />

        {/* User List */}
        <div className="glass-card rounded-xl p-4 mt-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /> Users ({tenantUsers.length})</h3>
          {tenantUsers.length === 0 ? (
            <p className="text-xs text-muted-foreground/70 italic py-2">No users found</p>
          ) : (
            <div className="space-y-1">
              {tenantUsers.map(u => (
                <div key={u.user_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border/20 hover:border-border/40 transition-colors group">
                  <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{u.name || 'Unnamed'}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Badge variant="outline" className="text-[8px] flex-shrink-0">{u.role}</Badge>
                  <Badge className={`text-[8px] flex-shrink-0 ${u.status === 'disabled' ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'}`}>{u.status || 'active'}</Badge>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      data-testid={`send-reset-btn-${u.user_id}`}
                      onClick={() => handleSendPasswordReset(u)}
                      className="text-[9px] px-2 py-0.5 rounded bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 flex items-center gap-1"
                      title="Send password reset email"
                    >
                      <KeyRound className="w-2.5 h-2.5" /> Reset
                    </button>
                    {isSuperAdmin && u.user_id !== user.user_id && (
                      <button
                        data-testid={`impersonate-btn-${u.user_id}`}
                        onClick={() => handleImpersonate(u)}
                        className="text-[9px] px-2 py-0.5 rounded bg-violet-500/10 text-violet-400 hover:bg-violet-500/20 flex items-center gap-1"
                        title="Impersonate this user for troubleshooting"
                      >
                        <UserCheck className="w-2.5 h-2.5" /> Impersonate
                      </button>
                    )}
                    {u.status !== 'disabled' ? (
                      <button data-testid={`disable-user-btn-${u.user_id}`} onClick={async () => { await api.updateUserStatus(u.user_id, { status: 'disabled' }); setTenantUsers(prev => prev.map(usr => usr.user_id === u.user_id ? { ...usr, status: 'disabled' } : usr)); toast.success('User disabled'); }} className="text-[9px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20" title="Disable user">Disable</button>
                    ) : (
                      <button data-testid={`enable-user-btn-${u.user_id}`} onClick={async () => { await api.updateUserStatus(u.user_id, { status: 'active' }); setTenantUsers(prev => prev.map(usr => usr.user_id === u.user_id ? { ...usr, status: 'active' } : usr)); toast.success('User enabled'); }} className="text-[9px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" title="Enable user">Enable</button>
                    )}
                    <button data-testid={`delete-user-btn-${u.user_id}`} onClick={async () => {
                      const ok = await confirmFn({
                        title: 'Delete user?',
                        message: `Delete ${u.email}? This cannot be undone.`,
                        confirmLabel: 'Delete',
                        destructive: true,
                      });
                      if (!ok) return;
                      await api.deleteUser(u.user_id);
                      setTenantUsers(prev => prev.filter(usr => usr.user_id !== u.user_id));
                      toast.success('User deleted');
                    }} className="text-[9px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20" title="Delete user">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add User Dialog */}
        <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add User to {tenantDetail.name}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-sm">Email</Label><Input value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Name</Label><Input value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Password</Label><Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-sm">Role</Label>
                <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="viewer">Viewer</SelectItem><SelectItem value="editor">Editor</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddUser} className="w-full gap-2"><Plus className="w-4 h-4" /> Add User</Button>
            </div>
          </DialogContent>
        </Dialog>
        <ConfirmDialogElement />
      </div>
    );
  }

  // Tenant list view (super_admin)
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="tenant-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold">Tenant Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage organizations, users, and AI credits</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2"><Plus className="w-4 h-4" /> New Tenant</Button>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-5">
        <Badge variant="secondary" className="gap-1.5 px-3 py-1"><Building2 className="w-3.5 h-3.5" /> {tenants.length} Tenants</Badge>
      </div>

      {/* Tenant Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tenants.map(t => (
          <button key={t.tenant_id} onClick={() => loadTenantDetail(t.tenant_id)}
            data-testid={`tenant-card-${t.tenant_id}`}
            className="glass-card rounded-xl p-5 text-left hover:border-primary/30 transition-all active:scale-[0.98] group">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 group-hover:bg-indigo-500/20 transition-colors">
                <Building2 className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-heading font-semibold">{t.name || t.tenant_id}</h3>
                <p className="text-xs text-muted-foreground">{t.domain || 'No domain'}</p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <Badge className={planColors[t.plan] || planColors.standard}>{t.plan || 'standard'}</Badge>
                <Badge variant="outline" className={`text-[8px] ${t.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400'}`}>{t.status || 'active'}</Badge>
              </div>
            </div>
            <div className="flex gap-4 mt-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {t.stats?.users || '?'} users</span>
              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {t.stats?.reports || '?'} reports</span>
              <span className="flex items-center gap-1"><Database className="w-3 h-3" /> {t.stats?.models || '?'} models</span>
              <ChevronRight className="w-3 h-3 ml-auto group-hover:text-primary" />
            </div>
          </button>
        ))}
      </div>

      {/* Create Tenant Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /> New Tenant</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-sm">Organization Name</Label><Input value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} placeholder="Acme Corp" className="mt-1" /></div>
            <div><Label className="text-sm">Domain</Label><Input value={newTenant.domain} onChange={e => setNewTenant(p => ({ ...p, domain: e.target.value }))} placeholder="acme.com" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Plan</Label>
                <Select value={newTenant.plan} onValueChange={v => setNewTenant(p => ({ ...p, plan: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="starter">Starter</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Max Users</Label><Input type="number" value={newTenant.max_users} onChange={e => setNewTenant(p => ({ ...p, max_users: Number(e.target.value) }))} className="mt-1" /></div>
            </div>
            <Button onClick={handleCreateTenant} className="w-full gap-2"><Plus className="w-4 h-4" /> Create Tenant</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialogElement />
    </div>
  );
}
